/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { CART_OVERLAY_POPUP_ID } from 'Component/CartOverlay/CartOverlay.config';
import { CART, CUSTOMER_WISHLIST, SEARCH } from 'Component/Header/Header.config';
import { SEARCH_POPUP_ID } from 'Component/SearchPopup/SearchPopup.config';
import { CHECKOUT_SUCCESS_URL, CHECKOUT_URL } from 'Route/Checkout/Checkout.config';
import { ACCOUNT_LOGIN_URL } from 'Route/MyAccount/MyAccount.config';
import {
    HeaderContainer as SourceHeaderContainer,
    mapDispatchToProps as sourceMapDispatchToProps,
    mapStateToProps as sourceMapStateToProps
} from 'SourceComponent/Header/Header.container';
import { ACCOUNT_URL } from 'SourceRoute/MyAccount/MyAccount.config';
import { toggleMenuMobile } from 'Store/Menu/Menu.action';
import { showPopup } from 'Store/Popup/Popup.action';
import { CustomerType } from 'Type/Account.type';
import { ItemType } from 'Type/ProductList.type';
import { isSignedIn } from 'Util/Auth';
import { toggleScroll } from 'Util/Browser';
import history from 'Util/History';
import { appendWithStoreCode, isHomePageUrl } from 'Util/Url';

export const MyAccountDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/MyAccount/MyAccount.dispatcher'
);

/** @namespace PeggysagePwa/Component/Header/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    ...sourceMapStateToProps(state),
    isWishlistEnabled: state.ConfigReducer.wishlist_general_active,
    isCustomerTypeStudentEnabled: state.ConfigReducer.students_active,
    wishlistItems: state.WishlistReducer.productsInWishlist,
    customer: state.MyAccountReducer.customer,
    isMenuMobileOpened: state.MenuReducer.isMenuMobileOpened
});

/** @namespace PeggysagePwa/Component/Header/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    ...sourceMapDispatchToProps(dispatch),
    requestCustomerData: () => MyAccountDispatcher.then(
        ({ default: dispatcher }) => dispatcher.requestCustomerData(dispatch)
    ),
    showCartOverlayPopup: (payload) => dispatch(showPopup(CART_OVERLAY_POPUP_ID, payload)),
    showSearchPopup: (payload) => dispatch(showPopup(SEARCH_POPUP_ID, payload)),
    toggleMenuMobile: (state) => dispatch(toggleMenuMobile(state))
});

/** @namespace PeggysagePwa/Component/Header/Container */
export class HeaderContainer extends SourceHeaderContainer {
    static propTypes = {
        ...SourceHeaderContainer.propTypes,
        isWishlistEnabled: PropTypes.bool.isRequired,
        isCustomerTypeStudentEnabled: PropTypes.bool.isRequired,
        wishlistItems: PropTypes.objectOf(ItemType),
        customer: CustomerType.isRequired,
        isMenuMobileOpened: PropTypes.bool.isRequired,
        requestCustomerData: PropTypes.func.isRequired,
        showCartOverlayPopup: PropTypes.func.isRequired,
        showSearchPopup: PropTypes.func.isRequired,
        toggleMenuMobile: PropTypes.func.isRequired
    };

    static defaultProps = {
        ...SourceHeaderContainer.defaultProps,
        wishlistItems: {}
    };

    state = {
        ...this.state,
        userGroupId: 0,
        isHomePage: false
    };

    containerFunctions = {
        ...this.containerFunctions,
        onMenuButtonClick: this.onMenuButtonClick.bind(this),
        onMenuClose: this.onMenuClose.bind(this),
        onWishlistButtonClick: this.onWishlistButtonClick.bind(this),
        onSwitcherButtonClick: this.onSwitcherButtonClick.bind(this),
        onSwitcherOutsideClick: this.onSwitcherOutsideClick.bind(this),
        onLogoClick: this.onLogoClick.bind(this)
    };

    /**
     * Override: Add props for mobile menu, set isCheckout to false on success page
     */
    containerProps() {
        const {
            isWishlistEnabled,
            isCustomerTypeStudentEnabled,
            wishlistItems,
            isMenuMobileOpened
        } = this.props;
        const { isSwitcherOpened, userGroupId, isHomePage } = this.state;

        const {
            location: {
                pathname
            }
        } = history;

        const isCheckout = pathname.includes(CHECKOUT_URL) && !pathname.includes(CHECKOUT_SUCCESS_URL);

        return {
            ...super.containerProps(),
            isCheckout,
            isMenuMobileOpened,
            isWishlistEnabled,
            isCustomerTypeStudentEnabled,
            wishlistItems,
            isSwitcherOpened,
            userGroupId,
            isHomePage
        };
    }

    /**
     * Override: Set default state value for mobile menu
     * @param props
     * @private
     */
    __construct(props) {
        super.__construct(props);

        this.state = {
            ...this.state,
            isSwitcherOpened: false,
            userGroupId: 0
        };
    }

    /**
     * Override: Re-enable scroll if switching from mobile to desktop device,
     * get user group id for UserIcon, set isHomePage prop, hide search overlay...
     * @param prevProps
     */
    componentDidUpdate(prevProps) {
        const {
            device: { isMobile: prevIsMobile },
            customer: prevCustomer,
            isMenuMobileOpened: prevIsMenuMobileOpened
        } = prevProps;
        const {
            device: { isMobile },
            requestCustomerData,
            customer,
            isMenuMobileOpened,
            toggleMenuMobile
        } = this.props;
        const { pathname } = location;
        const { prevPathname, searchCriteria } = this.state;

        // If menu mobile state change, toggle page scroll accordingly
        if (prevIsMenuMobileOpened !== isMenuMobileOpened && !searchCriteria) {
            toggleScroll(!isMenuMobileOpened);
        }

        if (prevIsMobile !== isMobile) {
            toggleScroll(true);
            toggleMenuMobile(false);
        }

        if (isSignedIn() && !Object.keys(customer).length) {
            requestCustomerData();
        }

        if (customer && customer !== prevCustomer) {
            this.setState({ userGroupId: customer.group_id || 0 });
        }

        // If the location changes, hide search overlay
        if (prevPathname !== pathname) {
            this.hideSearchOverlay();
            this.setState({ prevPathname: pathname });
        }

        this.setState({ isHomePage: isHomePageUrl(pathname) });

        super.componentDidUpdate(prevProps);
    }

    /**
     * Override: force back to cart on checkout
     * @param e
     */
    onBackButtonClick(e) {
        const { location: { pathname } } = history;

        const isCheckout = pathname.includes(CHECKOUT_URL);

        if (isCheckout) {
            history.push(appendWithStoreCode('/cart'));

            return;
        }

        super.onBackButtonClick(e);
    }

    /**
     * Override: Redirect to account url (disable overlay)
     */
    onMyAccountButtonClick() {
        const { toggleMenuMobile } = this.props;

        toggleMenuMobile(false);

        const url = isSignedIn() ? ACCOUNT_URL : ACCOUNT_LOGIN_URL;

        history.push({ pathname: appendWithStoreCode(url) });
    }

    /**
     * Override: open popup instead of overlay
     */
    onMinicartButtonClick() {
        const {
            // showOverlay,
            showCartOverlayPopup,
            device,
            toggleMenuMobile
        } = this.props;

        toggleMenuMobile(false);

        if (!device.isMobile) {
            // this.setState({ shouldRenderCartOverlay: true });

            // showOverlay(CART_OVERLAY);

            showCartOverlayPopup({
                title: __('My cart')
            });

            return;
        }

        history.push(appendWithStoreCode(`/${ CART }`));
    }

    onMenuButtonClick() {
        const { device: { isMobile }, isMenuMobileOpened, toggleMenuMobile } = this.props;

        if (!isMobile) {
            return;
        }

        toggleMenuMobile(!isMenuMobileOpened);
    }

    /**
     * Close mobile menu when a link is clicked
     */
    onMenuClose() {
        const { device: { isMobile }, toggleMenuMobile } = this.props;

        if (!isMobile) {
            return;
        }

        toggleMenuMobile(false);
    }

    onWishlistButtonClick() {
        const { toggleMenuMobile } = this.props;

        toggleMenuMobile(false);

        history.push({ pathname: appendWithStoreCode(CUSTOMER_WISHLIST) });
    }

    onSwitcherButtonClick() {
        const { isSwitcherOpened } = this.state;

        this.setState({ isSwitcherOpened: !isSwitcherOpened });
    }

    onSwitcherOutsideClick() {
        this.setState({ isSwitcherOpened: false });
    }

    onLogoClick() {
        const { toggleMenuMobile } = this.props;

        toggleMenuMobile(false);
    }

    /**
     * Override: pass variable to prevent blur
     * @param {boolean} blur
     */
    hideSearchOverlay(blur = true) {
        const { hideActiveOverlay, activeOverlay } = this.props;

        this.setState({ searchCriteria: '' });

        if (blur) {
            document.activeElement.blur();
        }

        if (activeOverlay === SEARCH) {
            hideActiveOverlay();
        }
    }

    /**
     * Override: open popup on desktop
     */
    onSearchBarFocus() {
        const {
            device,
            // activeOverlay,
            hideActiveOverlay,
            showOverlay,
            showSearchPopup
        } = this.props;

        if (device.isMobile) {
            hideActiveOverlay();
            showOverlay(SEARCH);
        } else {
            showSearchPopup({ title: '' });
        }
    }

    /**
     * Override: disable page scroll when search overlay is opened
     * @param searchCriteria
     */
    onSearchBarChange({ target: { value: searchCriteria } }) {
        const {
            device: { isMobile },
            hideActiveOverlay,
            activeOverlay,
            showOverlay
        } = this.props;

        if (isMobile) {
            if (searchCriteria) {
                toggleScroll(false);
                if (activeOverlay !== SEARCH) {
                    showOverlay(SEARCH);
                }
            } else {
                toggleScroll(true);
                hideActiveOverlay();
            }
        }

        this.setState({ searchCriteria });
    }

    /**
     * Override: re-enable page scroll after search clear
     */
    onClearSearchButtonClick() {
        const { device: { isMobile } } = this.props;

        if (isMobile) {
            toggleScroll(true);
        }

        super.onClearSearchButtonClick();
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HeaderContainer));
